import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { Factory } from 'src/app/utils/Factory';
import { ICreatedInstanceId, IQueryCacheService } from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardUsersActivity } from '../sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

export interface UsersdashActivityDailyDataLoaderFactoryOptions
    extends ICreatedInstanceId,
        IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId | (() => GcpProjectId);
    queryCacheService: QueryCacheService;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class UsersdashActivityDailyDataLoaderFactory extends Factory<
    DataLoader,
    UsersdashActivityDailyDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'UsersdashActivityDailyDataLoaderFactory';

    getInstance(options: UsersdashActivityDailyDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            // Resolve gcpProjectId to always be a value
            const resolvedGcpProjectId: GcpProjectId =
                typeof options.gcpProjectId === 'function'
                    ? options.gcpProjectId()
                    : options.gcpProjectId;

            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardUsersActivity.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                // TODO add context settings. Remember to update chart options build logic in order to support dynamic choice of day_no
                // const maxDayNo =
                //     queryContext.settings[
                //         QueryContextSettingID.ENGAGEMENT_MAX_DAY_NO
                //     ] ?? 30;
                // const selectedDayNoSQL: Partial<ICompiledSQLTokens> = {
                //     where: [`s_day_no <= ${maxDayNo}`],
                // };

                const {
                    dateTrunc,
                    splitSelect,
                    splitGroupBy,
                    where,
                    // is_in_context_app_version,
                    // unnestDatasourceColumn,
                    join,
                } = BiVizDashboardUsersActivity.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    { gcpProjectId: resolvedGcpProjectId }
                    // selectedDayNoSQL
                );

                // const selectFacts = is_in_context_app_version
                //     ? 'ds.date, ds.proper_id, app_version.*,'
                //     : 'ds.*,';

                const query = `
-- DataLoaderId: ${this.instanceId}

SELECT 
  ${splitSelect}date,
  COUNT(DISTINCT user_id) AS dau,
  COUNT(DISTINCT d0_user_id) AS new_users,
  FROM \`${generateTableFullPath(
      resolvedGcpProjectId,
      datasourceSqlMap.dataset,
      datasourceSqlMap.tableId
  )}\` ds
${join}
${where}
GROUP BY ${splitGroupBy}date
ORDER BY ${splitGroupBy}date ASC`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
